import type { LegendLabel } from './types.ts';

/**
 * Line colors for the Lines view and legend. High contrast against photos and distinct from
 * each other; each one stands for a product / price in the legend.
 */
export const LINE_COLORS = [
  { hex: '#22D3EE', name: 'Cyan' },
  { hex: '#F5A524', name: 'Amber' },
  { hex: '#E879F9', name: 'Magenta' },
  { hex: '#A3E635', name: 'Lime' },
  { hex: '#F43F5E', name: 'Red' },
  { hex: '#60A5FA', name: 'Blue' },
  { hex: '#F8FAFC', name: 'White' },
  { hex: '#FB923C', name: 'Orange' },
] as const;

export const DEFAULT_LINE_COLOR = LINE_COLORS[0].hex;

export function lineColorName(hex: string): string {
  return LINE_COLORS.find((c) => c.hex.toLowerCase() === hex.toLowerCase())?.name ?? 'Custom';
}

/** Palette order (unknown colors last), so the legend is stable. */
export function lineColorOrder(hex: string): number {
  const i = LINE_COLORS.findIndex((c) => c.hex.toLowerCase() === hex.toLowerCase());
  return i < 0 ? LINE_COLORS.length : i;
}

/**
 * The label for a line color, falling back to its color name with no price. FIXED behavior
 * (known issue 07 §B9): an exact key wins, else keys match case-insensitively ("#22d3ee" finds
 * "#22D3EE"). The original looked up the exact string only (`labelForExact`).
 */
export function labelFor(labels: Record<string, LegendLabel>, hex: string): LegendLabel {
  const exact = labels[hex];
  if (exact) return exact;
  const want = hex.toLowerCase();
  for (const key of Object.keys(labels)) if (key.toLowerCase() === want) return labels[key];
  return labelForExact({}, hex);
}

/** ORIGINAL `labelFor`: exact-string key lookup. */
export function labelForExact(labels: Record<string, LegendLabel>, hex: string): LegendLabel {
  return labels[hex] ?? { name: lineColorName(hex), product: '', pricePerFt: null };
}

/** '#22d3ee' → '#22D3EE'. Strings that are not #RRGGBB are returned unchanged. */
export function normalizeHex(hex: string): string {
  return /^#[0-9a-f]{6}$/i.test(hex) ? hex.toUpperCase() : hex;
}

/** Uppercases every legend key (later keys win on collisions). Use when importing legacy data. */
export function normalizeLegendKeys(labels: Record<string, LegendLabel>): Record<string, LegendLabel> {
  const out: Record<string, LegendLabel> = {};
  for (const [k, v] of Object.entries(labels)) out[normalizeHex(k)] = v;
  return out;
}

export const DEFAULT_LEGEND: Record<string, LegendLabel> = {
  [LINE_COLORS[0].hex]: { name: 'Signature', product: 'RGBW, 12 in spacing', pricePerFt: 28 },
  [LINE_COLORS[1].hex]: { name: 'Premium Pairs', product: 'RGBW pairs', pricePerFt: 36 },
  [LINE_COLORS[2].hex]: { name: 'Accent', product: 'Warm white', pricePerFt: 22 },
};
